import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '../../services/stripeService';
import { useLanguage } from '../../contexts/LanguageContext';

interface StripeProviderProps {
    children: React.ReactNode;
    clientSecret?: string;
    options?: Omit<StripeElementsOptions, 'clientSecret'>;
}

const StripeProvider: React.FC<StripeProviderProps> = ({ children, clientSecret, options }) => {
    const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
    const { language } = useLanguage();

    useEffect(() => {
        getStripe().then(setStripeInstance);
    }, []);

    const appearance = {
        theme: 'stripe' as const,
        variables: {
            colorPrimary: '#6138e0',
            colorBackground: '#ffffff',
            colorText: '#1a1a2e',
            colorDanger: '#e52d42',
            fontFamily: 'inherit',
            borderRadius: '8px',
        },
    };

    // AB#1639 / PR-D — pass the active language so Stripe renders its iframe
    // labels (Card number / Expiry / CVC) and inline error messages in
    // Arabic when LanguageContext === 'ar'. Without this the iframe stays
    // English-only inside an RTL parent, which is what the founder
    // flagged in UAT 2026-05-04. Stripe.js v3+ supports 'ar'.
    const stripeLocale = language === 'ar' ? 'ar' : 'en';

    // Stripe's StripeElementsOptions is a discriminated union between
    // ClientSecret / ModeSetup / ModeBase variants. TypeScript cannot narrow
    // the union from a conditional spread, so we cast at this boundary — the
    // runtime shape is correct by construction (clientSecret is included iff
    // truthy).
    const elementsOptions = (clientSecret
        ? { ...options, clientSecret, appearance, locale: stripeLocale }
        : { ...options, appearance, locale: stripeLocale }) as StripeElementsOptions;

    return (
        <Elements
            stripe={stripeInstance}
            options={clientSecret ? elementsOptions : { appearance, locale: stripeLocale }}
        >
            {children}
        </Elements>
    );
};

export default StripeProvider;
